/**
 * Mega menu intent layer (merchant 2026-10-31; NN/g mega-menu guidance, Apple / Nike behaviour).
 * Desktop mouse only; keyboard and touch keep the s-nav-dropdown button behaviour.
 * - Opens after the pointer rests ~300ms (Baymard: 300–500ms) on an item, so sweeping across the header never pops a panel.
 * - Closes 300ms after the pointer leaves, so a diagonal move toward the far columns does not lose the panel.
 * - While a panel is open, moving to another item swaps panels in place (no re-unroll).
 * - No page scrim (merchant 2026-10-31; .mega-scrim is hidden in CSS); Escape closes.
 * - Loads panel images (img[data-mega-src]) and the "Shop by material" chips on the first open only.
 */
const OPEN_DELAY = 300;
const CLOSE_DELAY = 300;
const MATERIALS_CACHE = "fz-mega-materials-v2";
const desktop = window.matchMedia('(hover: hover) and (min-width: 64rem)');
const items = [...document.querySelectorAll('s-nav-dropdown.nav-mega')];

if (items.length) {
  const root = document.documentElement;
  root.setAttribute('data-mega-js', '');

  const scrim = document.createElement('div');
  scrim.className = 'mega-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  document.body.append(scrim);

  let active = null;
  let openTimer = 0;
  let closeTimer = 0;

  const loadImages = (item) => {
    item.querySelectorAll('img[data-mega-src]').forEach((img) => {
      img.src = img.dataset.megaSrc;
      img.removeAttribute('data-mega-src');
    });
  };

  const loadMaterials = (item) => {
    const box = item.querySelector('[data-mega-materials][data-src]');
    if (!box) return;
    const src = box.dataset.src;
    box.removeAttribute('data-src');
    const row = box.closest('[data-mega-materials-row]');
    const put = (html) => {
      box.innerHTML = html;
      if (row) row.hidden = !html.trim();
    };
    try {
      const cached = sessionStorage.getItem(MATERIALS_CACHE);
      if (cached !== null) { put(cached); return; }
    } catch (e) { /* storage blocked */ }
    fetch(src, { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.text() : ''))
      .then((html) => {
        const clean = html.includes('<html') ? '' : html;
        put(clean);
        try { sessionStorage.setItem(MATERIALS_CACHE, clean); } catch (e) { /* storage full */ }
      })
      .catch(() => {});
  };

  const prepare = (item) => {
    loadImages(item);
    loadMaterials(item);
  };

  const anyButtonOpen = () => items.some((item) => item.hasAttribute('data-open'));

  const syncScrim = () => scrim.toggleAttribute('data-show', Boolean(active) || anyButtonOpen());

  const show = (item, instant) => {
    if (active === item) return;
    if (instant) root.setAttribute('data-mega-switching', '');
    active?.removeAttribute('data-mega-hover');
    active = item;
    prepare(item);
    item.setAttribute('data-mega-hover', '');
    syncScrim();
    if (instant) {
      requestAnimationFrame(() => requestAnimationFrame(() => root.removeAttribute('data-mega-switching')));
    }
  };

  const hide = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    active?.removeAttribute('data-mega-hover');
    active = null;
    syncScrim();
  };

  items.forEach((item) => {
    item.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !desktop.matches) return;
      clearTimeout(closeTimer);
      clearTimeout(openTimer);
      if (active) show(item, true);
      else openTimer = setTimeout(() => show(item, false), OPEN_DELAY);
    });
    item.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      clearTimeout(closeTimer);
      closeTimer = setTimeout(hide, CLOSE_DELAY);
    });
    // Keyboard / click opening (s-nav-dropdown sets data-open): load content and show the scrim too.
    new MutationObserver(() => {
      if (item.hasAttribute('data-open')) prepare(item);
      syncScrim();
    }).observe(item, { attributes: true, attributeFilter: ['data-open'] });
  });

  scrim.addEventListener('click', () => {
    hide();
    items.forEach((item) => {
      if (item.hasAttribute('data-open')) item.querySelector('[data-nav-disclosure]')?.click();
    });
    syncScrim();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && active) hide();
  });

  desktop.addEventListener('change', () => { if (!desktop.matches) hide(); });
}
